import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, IconButton, Tooltip } from '@mui/material';
import { MoonIcon, SunIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { ThemeMode } from 'constants/themes';
import { checkIsDarkTheme } from 'utils/theme';

import { setThemeModeAction } from 'store/auth/actions';
import { selectCurrentThemeMode } from 'store/global/selectors';

import {
  getContainerStyles,
  getIndicatorStyles,
  getSegmentStyles,
  tooltipSlotProps,
} from './ThemeToggle.styles';
import { ThemeToggleTestIds } from './ThemeToggle.testIds';

const ThemeToggle = ({ onSurface = false }) => {
  const dispatch = useDispatch();
  const themeMode = useSelector(selectCurrentThemeMode);
  const isDark = checkIsDarkTheme(themeMode);

  const handleSelect = useCallback(
    mode => () => {
      if (mode !== themeMode) {
        dispatch(setThemeModeAction(mode));
      }
    },
    [dispatch, themeMode],
  );

  return (
    <Box
      aria-label="Theme"
      data-testid={ThemeToggleTestIds.ROOT}
      role="group"
      sx={getContainerStyles(onSurface)}
    >
      <Box aria-hidden sx={getIndicatorStyles(isDark)} />

      <Tooltip slotProps={tooltipSlotProps} title="Light mode">
        <IconButton
          disableRipple
          aria-label="Light mode"
          aria-pressed={!isDark}
          data-testid={ThemeToggleTestIds.LIGHT}
          sx={getSegmentStyles(!isDark, onSurface)}
          onClick={handleSelect(ThemeMode.LIGHT)}
        >
          <SunIcon size={IconSize[18]} />
        </IconButton>
      </Tooltip>

      <Tooltip slotProps={tooltipSlotProps} title="Dark mode">
        <IconButton
          disableRipple
          aria-label="Dark mode"
          aria-pressed={isDark}
          data-testid={ThemeToggleTestIds.DARK}
          sx={getSegmentStyles(isDark, onSurface)}
          onClick={handleSelect(ThemeMode.DARK)}
        >
          <MoonIcon size={IconSize[18]} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

ThemeToggle.propTypes = {
  onSurface: PropTypes.bool,
};

export default memo(ThemeToggle);
